/**
 * Ref-less semantic snapshot renderer (fast-drive mode).
 *
 * The shared semantic formatter (`formatSemanticSnapshot`) gates element
 * emission on `[ref=...]` presence in ~10 places, so it renders nothing from a
 * ref-less `ariaSnapshot()`. This is a small, isolated renderer for the
 * fast-drive mode: it walks the parsed accessibility tree and emits
 * `role + accessible-name + structure`, with duplicate-label disambiguation
 * hints so the agent can author UNIQUE Playwright locators (`getByRole(...)`).
 *
 * It is a PURE projection of the already-captured YAML — no DOM/page/browser
 * access — which is the whole point of the mode (zero extra chatter).
 */
import { parseSnapshot, type SnapshotElement } from './snapshot-analyzer.js';
import { INTERACTIVE_ROLES, STRUCTURAL_ROLES } from './snapshot-formatter-shared.js';

export interface ReflessSnapshotOptions {
  /** Hard cap on rendered lines; a truncation marker is appended past it. */
  maxLines?: number;
  /** OR-matched ("Save|Email"), case-insensitive filter on role + name + scope. */
  search?: string;
}

const DEFAULT_MAX_LINES = 4000;
const SCOPE_LABEL_MAX = 40;

/**
 * Roles that act as a disambiguation "scope" — landmarks plus row/list/group
 * containers. A duplicate `button "Edit"` in two different scopes is told apart
 * by scope label ("in row Acme Corp") rather than a bare ordinal.
 */
const SCOPE_ROLES = new Set<string>([
  ...STRUCTURAL_ROLES,
  'row',
  'rowgroup',
  'listitem',
  'list',
  'group',
  'table',
  'grid',
  'treegrid',
  'tablist',
  'tabpanel',
  'menu',
  'menubar',
  'listbox',
]);

/** Roles whose visible text is informational and worth preserving for context. */
const TEXT_ROLES = new Set<string>([
  'heading',
  'paragraph',
  'text',
  'generic',
  'caption',
  'blockquote',
  'strong',
  'emphasis',
  'note',
  'status',
  'alert',
  // Table/grid data cells — emitted when they carry text so the agent can read
  // the table contents, not just the interactive elements inside it.
  'cell',
  'gridcell',
  'columnheader',
  'rowheader',
]);

/** Container roles emitted as structural context (their header/label is useful). */
const CONTAINER_ROLES = new Set<string>([
  'table',
  'grid',
  'treegrid',
  'list',
  'listbox',
  'menu',
  'menubar',
  'tablist',
]);

const ORDINALS = ['1st', '2nd', '3rd', '4th', '5th', '6th', '7th', '8th'];

interface FlatNode {
  el: SnapshotElement;
  depth: number;
  /** Nearest enclosing scope label (landmark/row/list), or null. */
  scopeLabel: string | null;
}

function clean(text: string | undefined): string {
  return (text ?? '').replace(/\s+/g, ' ').trim();
}

/** First non-empty descendant text, for naming an otherwise-unlabeled scope (e.g. a table row). */
function deriveScopeLabel(el: SnapshotElement): string | null {
  const own = clean(el.text);
  if (own) return own.slice(0, SCOPE_LABEL_MAX);
  // Bounded breadth-first scan for the first meaningful descendant text in
  // DOCUMENT ORDER (e.g. a table row's leftmost cell identifies the row). The
  // `shift()` is deliberate — `pop()` would surface the last child first and
  // pick the wrong scope label. The 200-guard bounds the cost.
  const stack = [...el.children];
  let guard = 0;
  while (stack.length > 0 && guard++ < 200) {
    const node = stack.shift()!;
    const t = clean(node.text);
    if (t) return t.slice(0, SCOPE_LABEL_MAX);
    stack.push(...node.children);
  }
  return null;
}

/** Whether this element gets its own rendered line (vs. being a transparent passthrough). */
function shouldEmit(el: SnapshotElement): boolean {
  if (INTERACTIVE_ROLES.has(el.role)) return true;
  if (STRUCTURAL_ROLES.has(el.role)) return true;
  if (CONTAINER_ROLES.has(el.role)) return true;
  if (el.role === 'heading') return true;
  if (TEXT_ROLES.has(el.role) && clean(el.text).length > 0) return true;
  return false;
}

/** Render a single element line (without indentation or disambiguation suffix). */
function renderLine(el: SnapshotElement): string {
  const name = clean(el.text);
  const parts: string[] = [el.role];
  if (el.role === 'heading' && el.level) parts[0] = `heading h${el.level}`;
  if (name) parts.push(`"${name}"`);
  let line = parts.join(' ');
  const value = clean(el.inputValue);
  if (value) line += `: ${value}`;
  return line;
}

/**
 * Render a ref-less `ariaSnapshot()` YAML into an agent-facing semantic snapshot
 * with duplicate-label disambiguation hints. Pure: no browser access.
 */
export function formatReflessSnapshot(yaml: string, opts?: ReflessSnapshotOptions): string {
  const roots = parseSnapshot(yaml);
  if (roots.length === 0) {
    return 'SEMANTIC SNAPSHOT (ref-less) — 0 elements\nPage appears empty or not loaded.';
  }

  // 1. Flatten to rendered nodes, tracking render depth and nearest scope.
  //    Non-emitting elements (generic/list/row containers) are transparent:
  //    they don't take a line or a depth level, but can still be a scope.
  const flat: FlatNode[] = [];
  const walk = (el: SnapshotElement, depth: number, scope: string | null): void => {
    const emit = shouldEmit(el);
    if (emit) flat.push({ el, depth, scopeLabel: scope });
    const childDepth = emit ? depth + 1 : depth;
    const nextScope = SCOPE_ROLES.has(el.role) ? (deriveScopeLabel(el) ?? scope) : scope;
    for (const child of el.children) walk(child, childDepth, nextScope);
  };
  for (const root of roots) walk(root, 0, null);

  // 2. Disambiguate duplicate interactive (role + name) — what the agent targets
  //    with getByRole. Prefer distinct scope labels; else positional ordinal.
  const groups = new Map<string, FlatNode[]>();
  for (const node of flat) {
    const name = clean(node.el.text);
    if (!name || !INTERACTIVE_ROLES.has(node.el.role)) continue;
    const key = `${node.el.role} ${name}`;
    const list = groups.get(key);
    if (list) list.push(node);
    else groups.set(key, [node]);
  }

  const suffixOf = new Map<FlatNode, string>();
  for (const nodes of groups.values()) {
    if (nodes.length < 2) continue;
    const scopes = nodes.map((n) => n.scopeLabel);
    const allDistinct =
      scopes.every((s) => s) && new Set(scopes).size === nodes.length;
    if (allDistinct) {
      for (const n of nodes) suffixOf.set(n, ` (in ${n.scopeLabel})`);
    } else {
      nodes.forEach((n, i) => {
        suffixOf.set(n, ` (${i < ORDINALS.length ? ORDINALS[i] : `${i + 1}th`})`);
      });
    }
  }

  // 3. Render each node to a line, then optionally filter by search terms
  //    (OR-matched, case-insensitive, on the rendered line + scope label).
  const rendered = flat.map((node) => ({
    scope: node.scopeLabel ?? '',
    line: '  '.repeat(node.depth) + renderLine(node.el) + (suffixOf.get(node) ?? ''),
  }));
  const terms = (opts?.search ?? '')
    .split('|')
    .map((t) => t.trim().toLowerCase())
    .filter(Boolean);
  const matched =
    terms.length === 0
      ? rendered
      : rendered.filter(
          ({ line, scope }) =>
            terms.some((t) => line.toLowerCase().includes(t) || scope.toLowerCase().includes(t))
        );

  // 4. Cap.
  const maxLines = opts?.maxLines ?? DEFAULT_MAX_LINES;
  const body = matched.slice(0, maxLines).map((m) => m.line);
  const truncated = Math.max(0, matched.length - body.length);

  const countLine =
    terms.length > 0
      ? `SEMANTIC SNAPSHOT (ref-less) — ${matched.length} of ${flat.length} elements matching "${opts!.search}"`
      : `SEMANTIC SNAPSHOT (ref-less) — ${flat.length} elements`;
  const header = [
    countLine,
    'Drive via Playwright code: page.getByRole(role, { name }) / getByLabel / getByText.',
    'Duplicate labels carry a hint — "(2nd)" → .nth(1); "(in X)" → scope the locator to X.',
    '---',
  ].join('\n');

  let out = `${header}\n${body.join('\n')}`;
  if (truncated > 0) {
    out += `\n… ${truncated} more elements truncated (narrow with search="term" or a more specific region).`;
  }
  return out;
}
